/**
 * Тематические наборы (21.08) — логика ЭКРАНА, ⛔ не подачи и не отбора слов.
 *
 * Наборов теперь три: трейдинг (окно 3, живёт в `trading.ts`) и два новых —
 * «Живой английский 18+» и «Разговорные выражения». Оба выключены по умолчанию,
 * оба включаются тумблером в настройках.
 *
 * Что живёт здесь:
 *   • строка РЕГИСТРА на карточке: сила ⬤⬤○ · с кем говорят · одна мягкая замена.
 *     ⭐⭐ Это и есть содержание набора: перевод человек часто знает и так, а насколько
 *     грубо и при ком — нет. Значения приезжают из ядра полем `card.register`,
 *     ⛔ витрина их не считает и не выдумывает;
 *   • правило отсечки 18+: спрашивать ли возраст ПЕРЕД включением. ⚠ Спрашиваем до
 *     нажатия, а не после отказа сервера: получить 400 в лицо — плохой способ узнать
 *     о правиле. ⛔ Но решает всё равно ЯДРО (`packs.set_pack`): без подтверждения оно
 *     не включит набор, даже если экран об этом забудет;
 *   • тексты тумблеров и подписи «сколько слов и как они придут».
 *
 * ⛔ Ни одной своей цифры: объём набора и дневная доза приходят в `/me.packs`.
 */

export type PackState = {
  key: string; name: string; title: string; adult: boolean; setting: string;
  daily_quota?: number; exists: boolean; enabled: boolean; words: number;
  asked?: boolean;
};

export type PacksBlock = { adult_confirmed: boolean; list: PackState[] };

/** Регистр слова — ровно то, что приезжает из ядра в `card.register`. */
export type Register = {
  pack: string; strength: number; company: string;
  softer?: string | null; note?: string | null;
};

/** Сила словами. ⭐ Три ступени — шкала Ofcom 2021, а не наша выдумка. */
export const STRENGTH_WORD: Record<number, string> = {
  0: "без грубости",
  1: "слабое",
  2: "грубое",
  3: "очень грубое",
};

/** Включён ли набор. ⚠ Приходит и числом, и строкой — та же грабля, что у звука. */
export function isOn(value: unknown): boolean {
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") return value === "1" || value === "true";
  return Boolean(value);
}

/** Падеж слова «слово»: 1 слово · 2 слова · 5 слов. */
export function plWords(n: number): string {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return "слов";
  if (b > 1 && b < 5) return "слова";
  if (b === 1) return "слово";
  return "слов";
}

/** Точки силы: ⬤⬤○ для 2 из 3. Сила 0 — три пустых, «это не брань». */
export function dots(strength: number): string {
  const n = Math.max(0, Math.min(3, Math.round(strength)));
  return "⬤".repeat(n) + "○".repeat(3 - n);
}

/**
 * ⭐ ГЛАВНАЯ СТРОКА КАРТОЧКИ. Одна, короткая, всегда в одном виде:
 *   «⬤⬤⬤ очень грубое · только со своими, и то в ссоре».
 * ⛔ Ни слова о морали и никаких «так говорить нельзя»: сказано, при ком уместно.
 */
export function registerLine(reg: Register | null | undefined): string {
  if (!reg || reg.strength === undefined || reg.strength === null) return "";
  const word = STRENGTH_WORD[Math.max(0, Math.min(3, Math.round(reg.strength)))] || "";
  const company = (reg.company || "").trim();
  return `${dots(reg.strength)} ${word}` + (company ? ` · ${company}` : "");
}

/**
 * «Мягче: …» — ОДНА замена и ТОЛЬКО при силе 2–3 (решение владельца 21.08).
 * У слабого слова заменять нечего, а у сильного это самое полезное на карточке.
 */
export function softerLine(reg: Register | null | undefined): string {
  if (!reg || !reg.softer || reg.strength < 2) return "";
  return `мягче: ${reg.softer}`;
}

/** ⚠ Ловушка — показывается ТОЛЬКО там, где она есть, ⛔ не «для полноты». */
export function noteLine(reg: Register | null | undefined): string {
  return (reg && reg.note) ? String(reg.note) : "";
}

/* --- тумблеры в настройках -------------------------------------------------- */

/** Показывать ли карточку набора: только если он у человека заведён. */
export function visible(state: PackState | null | undefined): boolean {
  return Boolean(state?.exists);
}

/** Нужно ли спросить возраст ПЕРЕД включением. Выключение — никогда. */
export function needsAgeConfirm(state: PackState | null | undefined,
                                adultConfirmed: boolean, turningOn: boolean): boolean {
  if (!state?.adult) return false;
  if (!turningOn) return false;                  // ⭐ выйти можно всегда
  return !adultConfirmed;
}

/** Подпись под тумблером: сколько слов в наборе и как они придут. */
export function summary(state: PackState | null | undefined): string {
  if (!state?.exists) return "Набор ещё не загружен.";
  const n = state.words || 0;
  const q = state.daily_quota || 0;
  const head = `В наборе ${n} ${plWords(n)}.`;
  if (!state.enabled) return `${head} Сейчас выключен — в занятии их нет.`;
  return `${head} Приходят по ${q} ${plWords(q)} в день, вперемешку с обычными.`;
}

/** Тексты карточки набора. ⭐ У взрослого набора — своя пометка и своё описание. */
export function packTexts(state: PackState | null | undefined) {
  const adult = Boolean(state?.adult);
  return {
    title: adult ? "Живой английский" : "Разговорные выражения",
    badge: adult ? "18+" : "",
    body: adult
      ? "Как ругаются на самом деле: досада, ссора, удивление. У каждого слова видно, "
        + "насколько оно грубое и при ком так говорят."
      : "То, что слышно в живой речи каждый день: «прекрати», «да ладно», «моя вина». "
        + "Без единого грубого слова.",
    on: "Вкл",
    off: "Выкл",
  };
}

/** Тексты подтверждения возраста. ⭐ Спрашиваем ОДИН раз — ответ хранит ядро. */
export function ageTexts() {
  return {
    title: "Тут настоящая брань",
    body: "В этом наборе слова, которые в жизни звучат грубо — включая самые сильные. "
      + "Мы показываем их как есть, вместе с пометкой, при ком так не говорят.",
    ask: "Мне есть 18 лет",
    no: "Не сейчас",
    foot: "Передумаете — тумблер всегда в настройках.",
  };
}
